import { useState } from 'react';
import { BarcodeType2, BarcodeType2Create } from '../types/costwand.d';
import { useBarcodeType2 } from '../hooks/useBarcodeType2.ts';
import BarcodeType2Form from '../components/BarcodeType2Form.tsx';
import BarcodeType2Table from '../components/BarcodeType2Table.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const BarcodeType2Page = () => {
  const {
    BarcodeType2,
    loading,
    error, // This error is for the overall fetch, not form submission
    currentPage,
    totalPages,
    totalItems, // Destructure totalItems from the hook
    handlePageChange,
    createBarcodeType2,
    updateBarcodeType2,
    deleteBarcodeType2,
    fetchBarcodeType2, // Assuming useBarcodeType2 provides a way to refetch
  } = useBarcodeType2();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingBarcodeType2, setEditingBarcodeType2] = useState<BarcodeType2 | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddBarcodeType2 = () => {
    setEditingBarcodeType2(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditBarcodeType2 = (barcodeType2: BarcodeType2) => {
    setEditingBarcodeType2(barcodeType2);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteBarcodeType2 = async (id: number) => {
    // The useBarcodeType2 hook should handle its own loading/error for delete
    await deleteBarcodeType2(id);
    // Refetch Barcode Type 2s after deletion to ensure the list is up-to-date
    fetchBarcodeType2(currentPage);
  };

  const handleFormSubmit = async (data: BarcodeType2Create) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingBarcodeType2) {
        success = await updateBarcodeType2(editingBarcodeType2.id, data);
      } else {
        success = await createBarcodeType2(data);
      }

      if (success) {
        setIsFormOpen(false); // Close form dialog on success
        fetchBarcodeType2(currentPage); // Re-fetch to update the list
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // This catch block is for unexpected errors not handled by the hook's return value
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingBarcodeType2(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Barcode Type 2 Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddBarcodeType2}>Add New Barcode Type 2</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingBarcodeType2 ? 'Edit Barcode Type 2' : 'Add New Barcode Type 2'}</DialogTitle>
              <DialogDescription>
                {editingBarcodeType2 ? 'Make changes to the barcode type 2 here.' : 'Add a new barcode type 2 to your list.'}
              </DialogDescription>
            </DialogHeader>
            <BarcodeType2Form
              initialData={editingBarcodeType2}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <BarcodeType2Table
        BarcodeType2={BarcodeType2}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems} // Pass totalItems to the table
        onPageChange={handlePageChange}
        onEdit={handleEditBarcodeType2}
        onDelete={handleDeleteBarcodeType2}
        loading={loading}
        error={error} // This error is for the table's data fetching
        baseEndpoint="/barcode_2" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default BarcodeType2Page;